import { createSlice } from '@reduxjs/toolkit';
import { showToast } from '@global/design-system/molecules';
import createFetchReducer from '../../utils/createFetchReducer';
import { ResponseAction } from '@app/redux/types';
import {
	getVoiceUserOptionsTypes,
	postVoiceTransformUserTypes,
} from '@app/redux/workspace/action';
import { VoiceUserOptionsResponse } from '@app/redux/workspace/types';
import { ToastMessage } from '@app/constants';
import { VOICE_TRANSFORM_USER_IMPOSSIBLE_ERROR_CODE } from '@app/constants/error';

export type PersonalizationStateType = {
	voiceUserOptions: VoiceUserOptionsResponse | null;
	voiceTransformUserResult: string | null;
	isPersonalizationImpossible: boolean;
	custom: Record<string, unknown>;
};

const initialState: PersonalizationStateType = {
	voiceUserOptions: null,
	voiceTransformUserResult: null,
	isPersonalizationImpossible: false,
	custom: {},
};

function getVoiceUserOptionsMapper(state: any, action: ResponseAction) {
	const { response } = action;
	state.voiceUserOptions = response as VoiceUserOptionsResponse;
}

function postVoiceTransformUserMapper(state: any, action: ResponseAction) {
	const { response } = action;
	state.voiceTransformUserResult =
		typeof response === 'string'
			? response
			: (response?.transformedText ?? null);
	state.isPersonalizationImpossible = false;
}

function postVoiceTransformUserErrorMapper(state: any, action: ResponseAction) {
	state.voiceTransformUserResult = null;
	state.isPersonalizationImpossible =
		action.error?.response?.data?.errorCode ===
		VOICE_TRANSFORM_USER_IMPOSSIBLE_ERROR_CODE;

	// Impossible personalization has an in-modal empty state; only toast unexpected failures.
	if (!state.isPersonalizationImpossible) {
		showToast({
			message: ToastMessage.error,
			type: 'error',
		});
	}
}

const getVoiceUserOptionsReducers = createFetchReducer(
	getVoiceUserOptionsTypes,
	getVoiceUserOptionsMapper,
);
const postVoiceTransformUserReducers = createFetchReducer(
	postVoiceTransformUserTypes,
	postVoiceTransformUserMapper,
	postVoiceTransformUserErrorMapper,
);

const personalizationSlice = createSlice({
	name: 'personalization',
	initialState,
	reducers: {
		resetVoiceTransformUserResult(state: PersonalizationStateType) {
			state.voiceTransformUserResult = null;
			state.isPersonalizationImpossible = false;
		},
	},
	extraReducers: {
		...getVoiceUserOptionsReducers,
		...postVoiceTransformUserReducers,
	},
});

export default personalizationSlice.reducer;
export const PersonalizationActions = personalizationSlice.actions;
